import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  X,
  RotateCcw,
  Package,
  Search,
  MapPin,
  CheckCircle,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { domain } from "../../../../security";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );
const inputCls =
  "block w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none transition focus:ring-2 focus:ring-red-200 focus:border-red-400";
const labelCls =
  "block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide";

// ==============================================================================
// STEP INDICATOR
// ==============================================================================
const StepIndicator = ({ step }) => {
  const steps = [
    { n: 1, label: "Select Order" },
    { n: 2, label: "Item Quantities" },
    { n: 3, label: "Review" },
  ];
  return (
    <div className="flex items-center">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step === s.n
                  ? "bg-red-600 text-white shadow-sm shadow-red-200"
                  : step > s.n
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
            >
              {step > s.n ? <CheckCircle size={14} /> : s.n}
            </div>
            <span
              className={`text-xs font-semibold hidden sm:block ${
                step === s.n
                  ? "text-red-600"
                  : step > s.n
                    ? "text-emerald-600"
                    : "text-slate-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-3 ${
                step > s.n ? "bg-emerald-300" : "bg-slate-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ==============================================================================
// MODAL FOR SELECTING SERIALS
// ==============================================================================
const SerialSelectionModal = ({
  isOpen,
  onClose,
  item,
  initialSelections,
  onSave,
}) => {
  // mapped as { serialNumberId: 'GOOD' | 'BAD' }
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (isOpen && item) {
      const map = {};
      (initialSelections?.goodSerialIds || []).forEach(
        (id) => (map[id] = "GOOD"),
      );
      (initialSelections?.badSerialIds || []).forEach(
        (id) => (map[id] = "BAD"),
      );
      setSelections(map);
    }
  }, [isOpen, item, initialSelections]);

  const toggleSerial = (id) => {
    setSelections((prev) => {
      const current = prev[id];
      if (!current) return { ...prev, [id]: "GOOD" };
      if (current === "GOOD") return { ...prev, [id]: "BAD" };

      // if BAD -> turn to NULL (remove)
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSave = () => {
    const goodIds = Object.keys(selections)
      .filter((k) => selections[k] === "GOOD")
      .map(Number);
    const badIds = Object.keys(selections)
      .filter((k) => selections[k] === "BAD")
      .map(Number);
    onSave(item.id, goodIds, badIds);
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 bg-slate-50">
          <div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="text-red-500" size={18} /> Select Serial
              Numbers
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{item.productName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto">
          <div className="flex gap-4 text-xs font-bold mb-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <div className="text-slate-400">
              Click a serial to cycle its condition:
            </div>
            <div className="text-emerald-600">1st Click = Good</div>
            <div className="text-orange-600">2nd Click = Damaged</div>
          </div>

          {!item.serials || item.serials.length === 0 ? (
            <div className="text-center py-10 text-slate-400 italic">
              No serial numbers found for this product.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {item.serials.map((s) => {
                const status = selections[s.serialNumberId];
                let btnCls =
                  "bg-white border-slate-300 text-slate-600 hover:bg-slate-50";

                if (status === "GOOD")
                  btnCls =
                    "bg-emerald-50 border-emerald-400 text-emerald-700 ring-1 ring-emerald-400";
                if (status === "BAD")
                  btnCls =
                    "bg-orange-50 border-orange-400 text-orange-700 ring-1 ring-orange-400";

                return (
                  <button
                    key={s.serialNumberId}
                    onClick={() => toggleSerial(s.serialNumberId)}
                    className={`px-3 py-2 text-xs font-mono font-bold border rounded-lg transition-all active:scale-95 shadow-sm ${btnCls}`}
                  >
                    {s.serialName || s.serialNumberId}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-xl"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 text-sm font-bold bg-slate-800 text-white rounded-xl hover:bg-slate-700"
          >
            Save Selections
          </button>
        </div>
      </div>
    </div>
  );
};

// ==============================================================================
// MAIN COMPONENT
// ==============================================================================
export default function AddDeliveryReturnModal({
  onClose,
  onSuccess,
  currentUserId,
}) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const [availableDOs, setAvailableDOs] = useState([]);
  const [selectedDOId, setSelectedDOId] = useState(null);
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");

  // Stores BOTH normal quantities and serialized arrays
  // { itemId: { goodQty: 0, badQty: 0, goodSerialIds: [], badSerialIds: [] } }
  const [itemReturns, setItemReturns] = useState({});

  // Serial Modal State
  const [activeSerialItem, setActiveSerialItem] = useState(null);

  useEffect(() => {
    const fetchDOs = async () => {
      try {
        const res = await axios.get(
          `${domain}/api/DeliveryReturns/available-dos`,
        );
        setAvailableDOs(res.data);
      } catch (err) {
        toast.error("Failed to load returnable orders.");
      } finally {
        setIsFetching(false);
      }
    };
    fetchDOs();
  }, []);

  const filteredDOs = useMemo(() => {
    return availableDOs.filter(
      (d) =>
        (d.deliveryOrderNumber || "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        (d.customerName || "").toLowerCase().includes(search.toLowerCase()),
    );
  }, [availableDOs, search]);

  const selectedDO = useMemo(
    () => availableDOs.find((d) => d.id === selectedDOId),
    [availableDOs, selectedDOId],
  );

  const handleSelectDO = (doId) => {
    setSelectedDOId(doId);
    const selected = availableDOs.find((d) => d.id === doId);
    if (selected && selected.items) {
      const initial = {};
      selected.items.forEach((item) => {
        initial[item.id] = {
          goodQty: "",
          badQty: "",
          goodSerialIds: [],
          badSerialIds: [],
        };
      });
      setItemReturns(initial);
    }
  };

  // ✅ Used for Non-Serialized Items (FIFO)
  const handleQtyChange = (itemId, field, value, maxQty) => {
    const num = parseInt(value) || 0;
    setItemReturns((prev) => {
      const current = prev[itemId] || { goodQty: "", badQty: "" };
      const otherField = field === "goodQty" ? "badQty" : "goodQty";
      const otherVal = parseInt(current[otherField]) || 0;

      if (num + otherVal > maxQty) {
        toast.warning(`Cannot exceed max delivered quantity of ${maxQty}`);
        return prev;
      }
      return { ...prev, [itemId]: { ...current, [field]: value } };
    });
  };

  // ✅ Triggered when SerialSelectionModal is saved
  const handleSerialSave = (itemId, goodIds, badIds) => {
    setItemReturns((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        goodSerialIds: goodIds,
        badSerialIds: badIds,
        // Auto-update visual quantities based on length
        goodQty: goodIds.length,
        badQty: badIds.length,
      },
    }));
    setActiveSerialItem(null);
  };

  // Metrics
  const totalItemsToReturn = Object.values(itemReturns).reduce(
    (sum, item) =>
      sum + (parseInt(item.goodQty) || 0) + (parseInt(item.badQty) || 0),
    0,
  );

  const totalRefundAmount =
    selectedDO?.items.reduce((sum, item) => {
      const qty =
        (parseInt(itemReturns[item.id]?.goodQty) || 0) +
        (parseInt(itemReturns[item.id]?.badQty) || 0);
      return sum + qty * (item.unitPrice || 0);
    }, 0) || 0;

  // Submit
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const returnItemsArray = Object.entries(itemReturns)
        .map(([itemId, qtys]) => ({
          DeliveryOrderItemId: parseInt(itemId),
          QuantityGood: parseInt(qtys.goodQty) || 0,
          QuantityBad: parseInt(qtys.badQty) || 0,
          GoodSerialNumberIds: qtys.goodSerialIds || [],
          BadSerialNumberIds: qtys.badSerialIds || [],
        }))
        .filter((x) => x.QuantityGood > 0 || x.QuantityBad > 0);

      const payload = {
        DeliveryOrderId: selectedDOId,
        Reason: reason,
        UserID: String(currentUserId), // Sent to UserContextDto
        Items: returnItemsArray,
      };

      await axios.post(`${domain}/api/DeliveryReturns`, payload);
      console.log("Final bulkData:", JSON.stringify(payload, null, 2));
      toast.success("Delivery Return processed successfully.");
      onSuccess();
    } catch (err) {
      console.error(err);
      // const msg =
      //   err.response?.data?.message || "Failed to create Delivery Order.";
      // const detailedMsg = err.response?.data?.message?.error || msg;
      toast.error(err.response?.data?.message || "Failed to process return.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
        <div className="relative flex h-full max-h-[85vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="shrink-0 border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-600 rounded-xl text-white">
                  <RotateCcw size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Process Split Return
                  </h2>
                  <p className="text-xs text-slate-500">
                    Formally track and log returned inventory per item condition
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>
            <StepIndicator step={step} />
          </div>

          <div className="flex-1 overflow-hidden flex flex-col min-h-0 bg-slate-50/50">
            {/* ── STEP 1 ── */}
            {step === 1 && (
              <div className="flex flex-col h-full min-h-0">
                <div className="shrink-0 px-6 py-3 border-b border-slate-100 bg-white">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search DO # or Customer..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className={inputCls + " pl-9 py-2.5 rounded-xl"}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {isFetching ? (
                    <div className="flex justify-center py-10">
                      <div className="animate-spin h-6 w-6 border-b-2 border-red-600 rounded-full"></div>
                    </div>
                  ) : (
                    filteredDOs.map((d) => (
                      <div
                        key={d.id}
                        onClick={() => handleSelectDO(d.id)}
                        className={`cursor-pointer p-4 border-b transition ${selectedDOId === d.id ? "bg-red-50/50 border-red-100" : "hover:bg-slate-50"}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${selectedDOId === d.id ? "border-red-600" : "border-slate-300"}`}
                            >
                              {selectedDOId === d.id && (
                                <div className="w-2.5 h-2.5 rounded-full bg-red-600"></div>
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-slate-800">
                                {d.deliveryOrderNumber}
                              </p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin size={12} /> {d.customerName} -{" "}
                                {d.locationName || "Unknown"}
                              </p>
                            </div>
                          </div>
                          <span className="font-bold text-slate-800">
                            {formatCurrency(d.totalAmount)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2 ── */}
            {step === 2 && (
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                  <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Package size={16} className="text-red-500" /> Products in{" "}
                    {selectedDO?.deliveryOrderNumber}
                  </h3>

                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500 font-bold">
                        <tr>
                          <th className="p-3 w-1/3">Product Name</th>
                          <th className="p-3 text-center">Delivered Qty</th>
                          <th className="p-3 text-center text-emerald-600">
                            Good Qty (Return)
                          </th>
                          <th className="p-3 text-center text-orange-600">
                            Damaged Qty (Return)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedDO?.items.map((item) => {
                          const returns = itemReturns[item.id] || {};
                          return (
                            <tr key={item.id} className="hover:bg-slate-50">
                              <td className="p-3 font-semibold text-slate-700">
                                {item.productName ||
                                  item.product?.name ||
                                  "Unknown"}
                                {item.hasSerial && (
                                  <span className="ml-2 text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-bold tracking-wider">
                                    SERIALIZED
                                  </span>
                                )}
                                { item?.brandName &&
                                  <div className="text-[10px] text-slate-400 mt-0.5">
                                    {item?.brandName || ""}
                                  </div>
                                }
                                <div className="text-[10px] text-slate-400 mt-0.5">
                                  ₱{(item.unitPrice || 0).toLocaleString()} each
                                </div>
                              </td>
                              <td className="p-3 text-center font-bold text-slate-800 text-lg border-r border-slate-100">
                                {item.quantity}
                              </td>

                              {/* ── DYNAMIC RENDERING (Inputs OR Button) ── */}
                              {item.hasSerial ? (
                                <td colSpan={2} className="p-3 bg-slate-50/50">
                                  <div className="flex items-center justify-between gap-3 px-2">
                                    <div className="text-xs font-bold flex gap-4">
                                      <span className="text-emerald-600">
                                        {returns.goodQty || 0} Good
                                      </span>
                                      <span className="text-orange-600">
                                        {returns.badQty || 0} Damaged
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => setActiveSerialItem(item)}
                                      className="px-4 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700 transition active:scale-95 shadow-sm flex gap-2 items-center"
                                    >
                                      <ShieldCheck size={14} /> Select Serials
                                    </button>
                                  </div>
                                </td>
                              ) : (
                                <>
                                  <td className="p-3 bg-emerald-50/20">
                                    <input
                                      type="number"
                                      min="0"
                                      max={item.quantity}
                                      value={returns.goodQty || ""}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          item.id,
                                          "goodQty",
                                          e.target.value,
                                          item.quantity,
                                        )
                                      }
                                      className="w-full text-center py-1.5 border border-emerald-200 rounded focus:ring-emerald-400 outline-none text-emerald-800 font-bold"
                                      placeholder="0"
                                    />
                                  </td>
                                  <td className="p-3 bg-orange-50/20">
                                    <input
                                      type="number"
                                      min="0"
                                      max={item.quantity}
                                      value={returns.badQty || ""}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          item.id,
                                          "badQty",
                                          e.target.value,
                                          item.quantity,
                                        )
                                      }
                                      className="w-full text-center py-1.5 border border-orange-200 rounded focus:ring-orange-400 outline-none text-orange-800 font-bold"
                                      placeholder="0"
                                    />
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="pt-2">
                    <label className={labelCls}>Reason for Return</label>
                    <textarea
                      rows="2"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className={inputCls + " resize-none"}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3 ── */}
            {step === 3 && (
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-5">
                  <div className="flex justify-between items-end border-b border-slate-100 pb-3 mb-4">
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase">
                        Target Order
                      </p>
                      <p className="font-bold text-slate-800 text-lg">
                        {selectedDO?.deliveryOrderNumber}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-slate-500 uppercase">
                        Total Cash Refund
                      </p>
                      <p className="font-bold text-red-600 text-2xl leading-none">
                        {formatCurrency(totalRefundAmount)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {selectedDO?.items.map((item) => {
                      const good = parseInt(itemReturns[item.id]?.goodQty) || 0;
                      const bad = parseInt(itemReturns[item.id]?.badQty) || 0;
                      if (good + bad === 0) return null;

                      return (
                        <div
                          key={item.id}
                          className="flex justify-between items-center text-sm p-3 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div>
                            <span className="font-bold text-slate-800 block">
                              {item.productName ||
                                item.product?.name ||
                                "Unknown"}
                            </span>
                            <div className="text-xs flex gap-3 mt-1 font-semibold">
                              {good > 0 && (
                                <span className="text-emerald-600">
                                  {good} Good
                                </span>
                              )}
                              {bad > 0 && (
                                <span className="text-orange-600">
                                  {bad} Damaged
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="font-bold text-red-600">
                            ₱{((good + bad) * item.unitPrice).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t bg-slate-50 px-6 py-4 flex items-center justify-between">
            <div className="text-xs text-slate-500 font-semibold text-red-600">
              {step === 2 && `${totalItemsToReturn} units mapped`}
            </div>
            <div className="flex gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => {
                    if (step === 2 && totalItemsToReturn === 0) {
                      toast.error("Select items to return");
                      return;
                    }
                    setStep((s) => s + 1);
                  }}
                  disabled={step === 1 && !selectedDOId}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition disabled:opacity-40 shadow-sm"
                >
                  Next <ArrowRight size={15} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition disabled:opacity-50 shadow-sm"
                >
                  {isLoading ? "Processing..." : "Process Return"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RENDER THE SUB-MODAL */}
      <SerialSelectionModal
        isOpen={!!activeSerialItem}
        onClose={() => setActiveSerialItem(null)}
        item={activeSerialItem}
        initialSelections={itemReturns[activeSerialItem?.id]}
        onSave={handleSerialSave}
      />
    </>
  );
}
